import { useTheme } from '@mui/material';
import type {
  ExternalProjectImportError,
  ExternalProjectImportPreview,
} from '@provision/entity-types';
import { ArrowRight, CircleAlert, CircleCheck } from 'lucide-react';
import {
  ModalList,
  type ModalListItem,
  type ModalListLoadMore,
} from 'src/components/ModalContainer';
import { getRecordKey, projectImportToString } from './import-results.utils';

interface ResultRow extends ModalListItem {
  addressId?: string;
}

interface ImportResultsListProps {
  records: ExternalProjectImportPreview[];
  errors?: ExternalProjectImportError<string | number>[];
  /** Project names by id, for the projects that failed to import. */
  projectNames: Map<string, string>;
  loading: boolean;
  loadMore?: ModalListLoadMore;
  /** Picking an imported project opens its site. */
  onOpenSite: (addressId: string) => void;
  /** Prefix of the filter and row test ids; an imported project's row is `-option-record-<id>`. */
  testId: string;
}

/** One row per project the import touched; the failed ones first, each with its reason. */
export const ImportResultsList = ({
  records,
  errors,
  projectNames,
  loading,
  loadMore,
  onOpenSite,
  testId,
}: ImportResultsListProps) => {
  const theme = useTheme();
  const hasBoth = Boolean(errors?.length) && records.length > 0;

  const errorRows: ResultRow[] = (errors ?? []).map(({ projectId, error }) => ({
    id: `error-${projectId}`,
    title: projectNames.get(String(projectId)) ?? `Project ${projectId}`,
    description: error,
    icon: <CircleAlert color={theme.palette.error.main} />,
    group: hasBoth ? 'Not imported' : undefined,
  }));

  const recordRows: ResultRow[] = records.map((record, index) => ({
    id: `record-${getRecordKey(record, index)}`,
    title: record.projectName,
    description: projectImportToString(record),
    icon: <CircleCheck color={theme.palette.success.main} />,
    endAdornment: record.addressId ? (
      <ArrowRight
        aria-label="Open site"
        size="0.875rem"
        color={theme.palette.custom.OV.sys.onSurfaceVariant}
      />
    ) : undefined,
    group: hasBoth ? 'Imported' : undefined,
    addressId: record.addressId,
  }));

  return (
    <ModalList
      items={[...errorRows, ...recordRows]}
      onSelect={({ addressId }) => {
        if (addressId) {
          onOpenSite(addressId);
        }
      }}
      loading={loading}
      loadMore={loadMore}
      placeholder="Search"
      emptyText="Nothing was imported."
      testId={testId}
    />
  );
};
